import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@apa/core-renderers/styles.css";
import { ComponentTypes, type All8PageContent } from "@apa/core-renderers";
import { createRuntimeImageComponent } from "../../Helper/storage";
import { reorderList } from "../LayoutEditor/utils";
import PageEditorComponentsPanel from "./PageEditorComponentsPanel";
import PageEditorMarkdownPanel from "./PageEditorMarkdownPanel";
import PageEditorJsonPanel from "./PageEditorJsonPanel";
import PageEditorPreviewPanel, { PREVIEW_MAX_WIDTH, PREVIEW_VIEWPORTS, type PreviewViewportKey } from "./PageEditorPreviewPanel";
import PageEditorComponentLibraryModal from "./PageEditorComponentLibraryModal";
import { componentLibrary, componentDefinitionMap, createComponentInstance, type ComponentDefinition, type Path } from "../../Pages/pageComponentLibrary";
import { componentsForPayload, normalizeComponents, parsePagePayload, serializePagePayload, setValueAtPath } from "./pageEditorUtils";
import { pageToEditableMarkdown } from "./pageToEditableMarkdown";
import { editableMarkdownToPage } from "./editableMarkdownToPage";

type ContentViewMode = "blocks" | "markdown" | "json";

type EmbeddedComponentsEditorProps = {
	label?: string;
	helper?: string;
	value?: any[] | null;
	onChange: (next: any[]) => void;
	disabled?: boolean;
};

const DEFAULT_LANGUAGE = "fr";
const MARKDOWN_PREVIEW_DEBOUNCE_MS = 800;

const buildDraft = (components: All8PageContent, label?: string, description?: string) => ({
	slug: "",
	language: DEFAULT_LANGUAGE,
	title: label ?? "",
	metadata: { title: label ?? "", description: description ?? "" },
	components
});

/** Embedded components editor with block, Markdown and JSON modes, and live preview. */
const EmbeddedComponentsEditor: React.FC<EmbeddedComponentsEditorProps> = ({
	label = "Components",
	helper,
	value,
	onChange,
	disabled = false
}) => {
	const [contentViewMode, setContentViewMode] = useState<ContentViewMode>("blocks");
	const [activeComponentKey, setActiveComponentKey] = useState<string | null>(null);
	const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>({});
	const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
	const [showLibrary, setShowLibrary] = useState(false);
	const [libraryView, setLibraryView] = useState<"grid" | "list" | "visual">("grid");
	const [librarySelection, setLibrarySelection] = useState<ComponentDefinition | null>(() => componentLibrary[0] ?? null);
	const [componentSearch, setComponentSearch] = useState("");
	const [componentTagFilters, setComponentTagFilters] = useState<string[]>([]);
	const [pageJsonDraft, setPageJsonDraft] = useState("");
	const [pageJsonError, setPageJsonError] = useState<string | null>(null);
	const [markdownDraft, setMarkdownDraft] = useState("");
	const [markdownError, setMarkdownError] = useState<string | null>(null);
	const [previewViewport, setPreviewViewport] = useState<PreviewViewportKey>("full");
	const [previewContainerWidth, setPreviewContainerWidth] = useState<number | null>(null);
	const previewContainerRef = useRef<HTMLDivElement | null>(null);
	const viewModeRef = useRef<ContentViewMode>(contentViewMode);
	const lastMarkdownKeyRef = useRef<string>("");
	const lastJsonKeyRef = useRef<string>("");
	const markdownDraftRef = useRef<string>("");
	const markdownPreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const markdownInsertRef = useRef<((def: ComponentDefinition) => void) | null>(null);

	const normalizedValue = useMemo(() => normalizeComponents(value), [value]);
	const [componentsState, setComponentsState] = useState<All8PageContent>(normalizedValue);

	useEffect(() => {
		setComponentsState(normalizedValue);
	}, [normalizedValue]);

	const commitComponents = useCallback(
		(next: All8PageContent) => {
			setComponentsState(next);
			onChange(componentsForPayload(next));
		},
		[onChange]
	);

	const previewComponents = useMemo<All8PageContent>(() => normalizeComponents(componentsState), [componentsState]);
	const availableLibrary = useMemo(
		() => componentLibrary.filter((definition) => definition.type !== ComponentTypes.DynamicComponents),
		[]
	);
	const normalizeTag = useCallback((value?: string | null) => (value ?? "").trim().toLowerCase(), []);
	const availableTags = useMemo(() => {
		const tags = new Set<string>();
		availableLibrary.forEach((definition) => {
			(definition.tags ?? []).forEach((tag) => {
				const normalized = normalizeTag(tag);
				if (normalized) tags.add(normalized);
			});
		});
		return Array.from(tags).sort((a, b) => a.localeCompare(b));
	}, [availableLibrary, normalizeTag]);
	const toggleTagFilter = useCallback((tag: string) => {
		const normalized = normalizeTag(tag);
		if (!normalized) return;
		setComponentTagFilters((prev) => {
			const next = new Set(prev);
			if (next.has(normalized)) {
				next.delete(normalized);
			} else {
				next.add(normalized);
			}
			return Array.from(next);
		});
	}, [normalizeTag]);
	const clearTagFilters = useCallback(() => setComponentTagFilters([]), []);

	useEffect(() => {
		setComponentTagFilters((prev) => prev.filter((tag) => availableTags.includes(tag)));
	}, [availableTags]);

	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({
			disableLinks: true,
			imageComponent: runtimeImageComponent
		}),
		[runtimeImageComponent]
	);
	const previewWidth = PREVIEW_VIEWPORTS[previewViewport];

	const previewCss = `
    .page-editor-preview {
      max-width: ${PREVIEW_MAX_WIDTH}px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }
    .page-editor-preview section {
      margin-left: 0 !important;
      margin-right: 0 !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
    }
    .page-editor-preview section[class*="mx-[calc(50%-50vw)"] {
      margin-left: auto !important;
      margin-right: auto !important;
      max-width: ${PREVIEW_MAX_WIDTH}px !important;
      width: 100% !important;
    }
    .page-editor-preview section .max-w-7xl {
      max-width: ${PREVIEW_MAX_WIDTH}px;
    }
  `;

	useEffect(() => {
		const el = previewContainerRef.current;
		if (!el) return;
		const updateWidth = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setPreviewContainerWidth(w);
		};
		updateWidth();
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			const w = entry?.contentRect?.width ?? 0;
			if (w > 0) setPreviewContainerWidth(w);
		});
		observer.observe(el);
		const raf = requestAnimationFrame(() => {
			requestAnimationFrame(updateWidth);
		});
		return () => {
			observer.disconnect();
			cancelAnimationFrame(raf);
		};
	}, [previewViewport]);

	const updateComponentValue = useCallback(
		(index: number, path: Path, value: any) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			if (!components[index]) return;
			const updatedComponent = setValueAtPath(components[index], path, value);
			const nextComponents = [...components];
			nextComponents[index] = updatedComponent;
			commitComponents(nextComponents);
		},
		[commitComponents, componentsState, disabled]
	);

	const removeComponent = useCallback(
		(index: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const nextComponents = components.filter((_, i) => i !== index);
			commitComponents(nextComponents);
		},
		[commitComponents, componentsState, disabled]
	);

	const moveComponent = useCallback(
		(from: number, to: number) => {
			if (disabled) return;
			const components = normalizeComponents(componentsState);
			const nextComponents = reorderList(components, from, to);
			commitComponents(nextComponents);
		},
		[commitComponents, componentsState, disabled]
	);

	const addComponentFromDefinition = useCallback(
		(definition: ComponentDefinition | null) => {
			if (!definition || disabled) return;
			if (contentViewMode === "markdown" && markdownInsertRef.current) {
				markdownInsertRef.current(definition);
				setShowLibrary(false);
				return;
			}
			const nextComponent = createComponentInstance(definition);
			const nextComponents = normalizeComponents([
				...componentsForPayload(previewComponents),
				nextComponent
			]);
			commitComponents(nextComponents);
			setShowLibrary(false);
		},
		[commitComponents, contentViewMode, disabled, previewComponents]
	);

	const filteredLibrary = useMemo(() => {
		const term = componentSearch.trim().toLowerCase();
		return availableLibrary.filter((definition) => {
			const matchesTerm =
				!term ||
				definition.title.toLowerCase().includes(term) ||
				definition.description.toLowerCase().includes(term) ||
				(definition.tags ?? []).some((tag) => tag.toLowerCase().includes(term));
			const definitionTags = (definition.tags ?? []).map(normalizeTag);
			const matchesTags =
				componentTagFilters.length === 0 ||
				definitionTags.some((tag) => componentTagFilters.includes(tag));
			return matchesTerm && matchesTags;
		});
	}, [componentSearch, availableLibrary, componentTagFilters, normalizeTag]);

	useEffect(() => {
		const currentType = librarySelection?.type;
		if (currentType && availableLibrary.some((definition) => definition.type === currentType)) return;
		setLibrarySelection(availableLibrary[0] ?? null);
	}, [availableLibrary, librarySelection]);

	useEffect(() => {
		if (libraryView === "visual") {
			const existingType = librarySelection?.type;
			const stillValid = existingType && filteredLibrary.some((def) => def.type === existingType);
			if (!stillValid) {
				setLibrarySelection(filteredLibrary[0] ?? null);
			}
		}
	}, [libraryView, filteredLibrary, librarySelection]);

	useEffect(() => {
		const componentsKey = JSON.stringify(componentsForPayload(previewComponents));
		const enteringJson = contentViewMode === "json" && viewModeRef.current !== "json";
		const componentsChangedWhileJson = contentViewMode === "json" && componentsKey !== lastJsonKeyRef.current;
		if (contentViewMode === "json" && (enteringJson || componentsChangedWhileJson)) {
			const draft = buildDraft(previewComponents, label, helper);
			setPageJsonDraft(serializePagePayload(draft, DEFAULT_LANGUAGE));
			setPageJsonError(null);
			lastJsonKeyRef.current = componentsKey;
		}

		const enteringMarkdown = contentViewMode === "markdown" && viewModeRef.current !== "markdown";
		const componentsChanged = contentViewMode === "markdown" && componentsKey !== lastMarkdownKeyRef.current;
		if (contentViewMode === "markdown" && (enteringMarkdown || componentsChanged)) {
			const draft = buildDraft(previewComponents, label, helper);
			const md = pageToEditableMarkdown(draft, DEFAULT_LANGUAGE, { includeFrontmatter: false });
			markdownDraftRef.current = md;
			setMarkdownDraft(md);
			setMarkdownError(null);
			lastMarkdownKeyRef.current = componentsKey;
		}

		if (contentViewMode !== "markdown" && viewModeRef.current === "markdown") {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			try {
				const nextDraft = editableMarkdownToPage(
					markdownDraftRef.current,
					buildDraft(previewComponents, label, helper),
					DEFAULT_LANGUAGE
				);
				commitComponents(normalizeComponents(nextDraft.components));
				setMarkdownError(null);
			} catch {
				// ignore parse errors when leaving
			}
		}

		viewModeRef.current = contentViewMode;
		return () => {
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
		};
	}, [contentViewMode, commitComponents, helper, label, previewComponents]);

	const handleMarkdownChange = useCallback(
		(nextValue: string) => {
			if (disabled) return;
			markdownDraftRef.current = nextValue;
			setMarkdownDraft(nextValue);
			if (markdownPreviewDebounceRef.current) {
				clearTimeout(markdownPreviewDebounceRef.current);
				markdownPreviewDebounceRef.current = null;
			}
			markdownPreviewDebounceRef.current = setTimeout(() => {
				markdownPreviewDebounceRef.current = null;
				try {
					const nextDraft = editableMarkdownToPage(
						nextValue,
						buildDraft(previewComponents, label, helper),
						DEFAULT_LANGUAGE
					);
					commitComponents(normalizeComponents(nextDraft.components));
					setMarkdownError(null);
				} catch (err) {
					setMarkdownError(err instanceof Error ? err.message : "Markdown invalid.");
				}
			}, MARKDOWN_PREVIEW_DEBOUNCE_MS);
		},
		[commitComponents, disabled, helper, label, previewComponents]
	);

	const handlePageJsonChange = useCallback(
		(nextValue: string) => {
			if (disabled) return;
			setPageJsonDraft(nextValue);
			try {
				const nextDraft = parsePagePayload(
					nextValue,
					buildDraft(previewComponents, label, helper),
					DEFAULT_LANGUAGE
				);
				commitComponents(normalizeComponents(nextDraft.components));
				setPageJsonError(null);
			} catch {
				setPageJsonError("JSON not valid.");
			}
		},
		[commitComponents, disabled, helper, label, previewComponents]
	);

	const handleFormatJson = useCallback(() => {
		try {
			const formatted = JSON.stringify(JSON.parse(pageJsonDraft), null, 2);
			handlePageJsonChange(formatted);
		} catch {
			setPageJsonError("JSON not valid.");
		}
	}, [pageJsonDraft, handlePageJsonChange]);

	const editorOptions = useMemo(
		() => ({
			minimap: { enabled: false },
			lineNumbers: "on" as const,
			folding: true,
			showFoldingControls: "always" as const,
			formatOnPaste: true,
			formatOnType: true,
			wordWrap: "on" as const,
			scrollBeyondLastLine: false,
			automaticLayout: true,
			tabSize: 2
		}),
		[]
	);

		const editorPanel =
			contentViewMode === "blocks" ? (
				<PageEditorComponentsPanel
					editable={!disabled}
					previewComponents={previewComponents}
					componentDefinitionMap={componentDefinitionMap}
					activeComponentKey={activeComponentKey}
					setActiveComponentKey={setActiveComponentKey}
					updateComponentValue={updateComponentValue}
					removeComponent={removeComponent}
					moveComponent={moveComponent}
					jsonDrafts={jsonDrafts}
					setJsonDrafts={setJsonDrafts}
					jsonErrors={jsonErrors}
					setJsonErrors={setJsonErrors}
					onOpenLibrary={() => setShowLibrary(true)}
				/>
			) : contentViewMode === "markdown" ? (
				<PageEditorMarkdownPanel
					editable={!disabled}
					value={markdownDraft}
					error={markdownError}
					onChange={handleMarkdownChange}
					editorOptions={editorOptions}
					editorHeight={450}
					componentDefinitionMap={componentDefinitionMap}
					pageTitle={label}
					pageDescription={helper}
					onOpenComponentLibrary={() => setShowLibrary(true)}
					insertComponentRef={markdownInsertRef}
				/>
			) : (
				<PageEditorJsonPanel
					editable={!disabled}
					pageJsonDraft={pageJsonDraft}
					pageJsonError={pageJsonError}
					onFormat={handleFormatJson}
					onChange={handlePageJsonChange}
					jsonEditorOptions={editorOptions}
					editorHeight={450}
				/>
			);

		return (
			<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">{label}</p>
					{helper && <p className="text-sm text-slate-600">{helper}</p>}
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => setContentViewMode("blocks")}
						className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
							contentViewMode === "blocks"
								? "border border-slate-800 bg-slate-800 text-white shadow-sm"
								: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
						}`}
					>
						Blocks
					</button>
					<button
						type="button"
						onClick={() => setContentViewMode("markdown")}
						className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
							contentViewMode === "markdown"
								? "border border-slate-800 bg-slate-800 text-white shadow-sm"
								: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
						}`}
					>
						Markdown
					</button>
					<button
						type="button"
						onClick={() => setContentViewMode("json")}
						className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
							contentViewMode === "json"
								? "border border-slate-800 bg-slate-800 text-white shadow-sm"
								: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
						}`}
					>
						JSON
					</button>
				</div>
			</div>

			<div className="grid gap-5 md:grid-cols-2">
				<div className="min-w-0">{editorPanel}</div>
				<div className="min-w-0">
					<PageEditorPreviewPanel
						previewViewport={previewViewport}
						onViewportChange={setPreviewViewport}
						previewPageUrl=""
						productionPageUrl=""
						previewContainerRef={previewContainerRef}
						previewCss={previewCss}
						previewComponents={previewComponents}
						previewWidth={previewWidth}
						previewContainerWidth={previewContainerWidth}
						runtimeConfig={runtimeConfig}
				minHeight={450}
				contentHeight={450}
					/>
				</div>
			</div>

			<PageEditorComponentLibraryModal
				show={showLibrary}
				onClose={() => setShowLibrary(false)}
				libraryView={libraryView}
				setLibraryView={setLibraryView}
				componentSearch={componentSearch}
				setComponentSearch={setComponentSearch}
				filteredLibrary={filteredLibrary}
				availableTags={availableTags}
				activeTags={componentTagFilters}
				onToggleTag={toggleTagFilter}
				onClearTags={clearTagFilters}
				librarySelection={librarySelection}
				setLibrarySelection={setLibrarySelection}
				addComponentFromDefinition={addComponentFromDefinition}
				runtimeConfig={runtimeConfig}
			/>
		</div>
	);
};

export default EmbeddedComponentsEditor;
